// ============================================================================
// TAX MASTER MODULE — local wire types (ADR-0019 §8)
// ============================================================================
// Mirrors the API template Tax module's models for the /api/v1/tax-masters
// contract. Declared locally so the module stays relocatable: it references
// nothing outside its folder except template-standard components (see README).

export interface TaxMasterComponentRate {
  componentCode: string;
  ratePercentage: number;
}

export interface TaxMasterBand {
  /** Inclusive; null on a flat master's single band. */
  valueFrom: number | null;
  /** Inclusive; null = open-ended top band. */
  valueTo: number | null;
  components: TaxMasterComponentRate[];
}

export interface TaxMasterSummary {
  rateCode: string;
  name: string;
  kind: 'FLAT' | 'SLAB' | string;
  hasCess: boolean;
  supplyCategory: string;
  rateSummary: string;
  versionCount: number;
  inForceFrom: string | null;
  nextChangeOn: string | null;
}

export interface TaxMasterVersion {
  rateCode: string;
  effectiveFrom: string;
  isInForce: boolean;
  isFuture: boolean;
  isSuperseded: boolean;
  conditionKey: string | null;
  bands: TaxMasterBand[];
}

/**
 * The list query — the template's standard paging / search / sort plus one filter per grid
 * column, so the screen's filter panel maps one-to-one onto the server (CLAUDE.md, "Filter
 * panel includes a filter field for every grid column"). Dates are ISO `yyyy-MM-dd`.
 */
export interface TaxMasterQuery {
  page?: number;
  pageSize?: number;
  /** Matches name and code, server-side. */
  searchTerm?: string;
  /** name | rateCode | kind | supplyCategory | hasCess | rateSummary | versionCount | inForceFrom | nextChangeOn */
  sortBy?: string;
  sortDescending?: boolean;
  kind?: 'FLAT' | 'SLAB' | string;
  hasCess?: boolean;
  supplyCategory?: string;
  inForceFrom?: string;
  inForceTo?: string;
  nextChangeFrom?: string;
  nextChangeTo?: string;
  hasScheduledChange?: boolean;
}

/** One server page — the API template's PagedResult shape. */
export interface TaxMasterPage {
  items: TaxMasterSummary[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface CreateTaxMasterRequest {
  rateCode: string;
  name: string;
  supplyCategory: string;
  effectiveFrom: string;
  conditionKey?: string | null;
  bands: TaxMasterBand[];
  notificationReference?: string | null;
}

export interface AppendTaxMasterVersionRequest {
  effectiveFrom: string;
  bands: TaxMasterBand[];
  notificationReference?: string | null;
  reason?: string | null;
}

/** A published slab basis the editor offers (from the jurisdiction pack, ADR-0021). */
export interface TaxMasterBasisOption {
  value: string;
  label: string;
  /** Shown as the option hint: unit, scope. */
  description?: string | null;
}

/**
 * Injected transport — the host app wires this to its own HTTP client against the shared
 * /api/v1/tax-masters contract. Rejections should carry the server's message (it IS the user
 * guidance; the module surfaces it verbatim).
 */
export interface TaxMasterApi {
  /** Server-side paged, searched, filtered and sorted list. */
  list(jurisdictionCode: string, query?: TaxMasterQuery): Promise<TaxMasterPage>;
  get(jurisdictionCode: string, rateCode: string): Promise<TaxMasterVersion[]>;
  create(jurisdictionCode: string, request: CreateTaxMasterRequest): Promise<void>;
  appendVersion(jurisdictionCode: string, rateCode: string, request: AppendTaxMasterVersionRequest): Promise<void>;
  replaceVersion(jurisdictionCode: string, rateCode: string, effectiveFrom: string, request: AppendTaxMasterVersionRequest): Promise<void>;
  deleteVersion(jurisdictionCode: string, rateCode: string, effectiveFrom: string, reason?: string): Promise<void>;
  /**
   * CSV of every master matching the query (no paging). Optional: a host without the EXPORT
   * permission seeded hides the button when the API is absent or the user lacks the right.
   */
  exportCsv?(jurisdictionCode: string, query?: TaxMasterQuery): Promise<Blob>;
}

export function apiErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || 'Request failed.';
}
